import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { HomeOwnerMatchingProfile } from "../../../__generated__/types";
import { ACCENT_COLOR, DESIGN_STYLE_IMAGE_COUNT } from "../constants";
import { CONCEPT_PRESENTATION_LABELS } from "../data";
import { getStyleDescription, getStyleImageCandidates, toTitleCase } from "../utils";
import { useGetIdeaBooks } from "../../../hooks/idea-books/use-get-idea-books";
import { useGetIdeaBook } from "../../../hooks/idea-books/use-get-idea-book";
import { useGetIdeaBookItems } from "../../../hooks/idea-books/use-get-idea-book-items";
import type { IdeaBook, IdeaBookItem } from "../../../hooks/idea-books/types";
import { useNotification } from "../../../contexts/NotificationContext";
import { useUserContext } from "../../../hooks/userContext";
import { Pages } from "../../../utils";

type ResponsiveSize =
  | number
  | string
  | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };

type DesignStyleResult = {
  key: string;
  displayName: string;
  description: string;
  imageStyles: string[];
};

const StyleImageTile = ({
  candidates,
  alt,
  height = 150,
}: {
  candidates: string[];
  alt: string;
  height?: ResponsiveSize;
}) => {
  const [index, setIndex] = useState(0);
  const src = candidates[index] ?? "";
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={() => {
        setIndex((prev) => (prev + 1 < candidates.length ? prev + 1 : prev));
      }}
      sx={{
        width: "100%",
        height,
        borderRadius: 2,
        objectFit: "cover",
        border: "1px solid #E5E7EB",
        backgroundColor: "#F3F4F6",
      }}
    />
  );
};

type DesignStyleTabProps = {
  profile: HomeOwnerMatchingProfile | null;
};

const IDEA_BOOKS_PAGE_SIZE = 4;

const DesignStyleTab = ({ profile }: DesignStyleTabProps) => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { showError } = useNotification();
  const { getIdeaBooks } = useGetIdeaBooks();
  const { getIdeaBook } = useGetIdeaBook();
  const { getIdeaBookItems } = useGetIdeaBookItems();
  const [ideaBooks, setIdeaBooks] = useState<IdeaBook[]>([]);
  const [isLoadingIdeaBooks, setIsLoadingIdeaBooks] = useState(false);
  const [ideaBooksPage, setIdeaBooksPage] = useState(0);
  const [ideaBookDialogOpen, setIdeaBookDialogOpen] = useState(false);
  const [activeIdeaBook, setActiveIdeaBook] = useState<IdeaBook | null>(null);
  const [activeIdeaBookItems, setActiveIdeaBookItems] = useState<IdeaBookItem[]>([]);
  const [isLoadingIdeaBookDetail, setIsLoadingIdeaBookDetail] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const designStyleValues = useMemo(() => {
    const values = (profile?.homeOwnerStyles?.homeOwnerStyle ?? []) as Array<
      string | null | undefined
    >;
    return values.filter((value): value is string => Boolean(value)).map(String);
  }, [profile?.homeOwnerStyles?.homeOwnerStyle]);

  const designStyleResults = useMemo<DesignStyleResult[]>(() => {
    if (!designStyleValues.length) {
      return [];
    }
    return designStyleValues.map((styleValue) => {
      const trimmed = styleValue.trim();
      if (trimmed.includes("+")) {
        const parts = trimmed
          .split("+")
          .map((part) => part.trim())
          .filter(Boolean);
        const displayName = parts.map(toTitleCase).join(" + ");
        const [primary, secondary] = parts;
        const description =
          primary && secondary
            ? `A unique fusion of ${toTitleCase(primary).toLowerCase()} and ${toTitleCase(
                secondary
              ).toLowerCase()} styles: ${getStyleDescription(primary)} meets ${getStyleDescription(
                secondary
              )}. Together, they create a harmonious blend of both aesthetics.`
            : getStyleDescription(trimmed);
        return {
          key: trimmed,
          displayName,
          description,
          imageStyles: parts.length ? parts : [trimmed],
        };
      }
      return {
        key: trimmed,
        displayName: toTitleCase(trimmed),
        description: getStyleDescription(trimmed),
        imageStyles: [trimmed],
      };
    });
  }, [designStyleValues]);

  const designStyleImageIndices = useMemo(() => {
    const favoriteLook = Number(profile?.homeOwnerStyles?.favoriteLook ?? 1);
    const start =
      Number.isFinite(favoriteLook) && favoriteLook > 0 ? favoriteLook : 1;
    return Array.from({ length: DESIGN_STYLE_IMAGE_COUNT }, (_, index) => start + index);
  }, [profile?.homeOwnerStyles?.favoriteLook]);

  const designConceptLabels = useMemo(() => {
    const conceptValue = profile?.projectType?.conceptPresentation;
    const values = Array.isArray(conceptValue)
      ? conceptValue
      : conceptValue
      ? [String(conceptValue)]
      : [];
    return values
      .filter(Boolean)
      .map((value) => CONCEPT_PRESENTATION_LABELS[value] ?? toTitleCase(value));
  }, [profile?.projectType?.conceptPresentation]);

  const showPreferredRendering = useMemo(
    () => designConceptLabels.some((label) => label.toLowerCase().includes("3d")),
    [designConceptLabels]
  );

  useEffect(() => {
    const homeownerId = profile?.homeOwnerId ?? user?.id;
    if (!homeownerId) {
      setIdeaBooks([]);
      setIsLoadingIdeaBooks(false);
      return;
    }

    let isActive = true;
    setIsLoadingIdeaBooks(true);
    getIdeaBooks(homeownerId, true)
      .then((books) => {
        if (!isActive) return;
        setIdeaBooks(books);
      })
      .catch(() => {
        if (!isActive) return;
        showError("Unable to load idea books.");
      })
      .finally(() => {
        if (!isActive) return;
        setIsLoadingIdeaBooks(false);
      });

    return () => {
      isActive = false;
    };
  }, [getIdeaBooks, profile?.homeOwnerId, showError, user?.id]);

  const maxIdeaBooksPage = useMemo(() => {
    const totalPages = Math.ceil(ideaBooks.length / IDEA_BOOKS_PAGE_SIZE);
    return Math.max(0, totalPages - 1);
  }, [ideaBooks.length]);

  useEffect(() => {
    setIdeaBooksPage((prev) => Math.min(prev, maxIdeaBooksPage));
  }, [maxIdeaBooksPage]);

  const visibleIdeaBooks = useMemo(() => {
    const start = ideaBooksPage * IDEA_BOOKS_PAGE_SIZE;
    return ideaBooks.slice(start, start + IDEA_BOOKS_PAGE_SIZE);
  }, [ideaBooks, ideaBooksPage]);

  const canGoPrev = ideaBooksPage > 0;
  const canGoNext = ideaBooksPage < maxIdeaBooksPage;
  const isDesignerViewer = user?.accountType === "Designer";

  const handleOpenIdeaBook = async (book: IdeaBook) => {
    if (!book?._id) return;
    setIdeaBookDialogOpen(true);
    setIsLoadingIdeaBookDetail(true);
    setActiveIdeaBook(null);
    setActiveIdeaBookItems([]);
    try {
      const [detail, items] = await Promise.all([
        getIdeaBook(book._id, true),
        getIdeaBookItems(book._id, true),
      ]);
      setActiveIdeaBook(detail ?? book);
      setActiveIdeaBookItems(items ?? []);
    } catch (error) {
      showError("Unable to load idea book details.");
    } finally {
      setIsLoadingIdeaBookDetail(false);
    }
  };

  const handleCloseIdeaBook = () => {
    setIdeaBookDialogOpen(false);
    setActiveIdeaBook(null);
    setActiveIdeaBookItems([]);
  };

  return (
    <Stack
      spacing={{ xs: 4, md: 8 }}
      sx={{ pt: { xs: 2, md: 3 }, px: { xs: 2, md: 0 } }}
    >
      <Typography
        align="center"
        sx={{
          color: "#6B7280",
          fontSize: "0.95rem",
        }}
      >
        Visual preferences, style inspirations, and how the homeowner wants to experience the space.
      </Typography>

      <Grid container spacing={4} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          <Box sx={{ px: { xs: 0, md: "100px" }, pb: { xs: 6, md: "100px" }, pt: 0 }}>
          <Stack spacing={{ xs: 4, md: 8 }}>
            <Stack spacing={3}>
              {designStyleResults.length ? (
                designStyleResults.map((result) => {
                  const [firstIndex, secondIndex, thirdIndex] =
                    designStyleImageIndices;
                  const primaryIndex = firstIndex ?? 1;
                  const secondaryIndex = secondIndex ?? primaryIndex + 1;
                  const tertiaryIndex = thirdIndex ?? secondaryIndex + 1;
                  const isHybrid = result.imageStyles.length > 1;
                  return (
                    <Stack
                      key={result.key}
                      spacing={2}
                      sx={{ textAlign: "center" }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: ACCENT_COLOR,
                          fontSize: "1.4rem",
                        }}
                      >
                        {result.displayName}
                      </Typography>
                      {result.description ? (
                        <Typography sx={{ color: "#6B7280" }}>
                          {result.description}
                        </Typography>
                      ) : null}
                      {isHybrid ? (
                        <Grid container spacing={2}>
                          {result.imageStyles
                            .slice(0, 2)
                            .map((styleValue, idx) => (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                key={`${result.key}-${styleValue}-${idx}`}
                              >
                                <StyleImageTile
                                  candidates={getStyleImageCandidates(
                                    styleValue,
                                    primaryIndex
                                  )}
                                  alt={`${result.displayName} style ${idx + 1}`}
                                  height={{ xs: 220, md: 300 }}
                                />
                              </Grid>
                            ))}
                        </Grid>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: "stretch",
                          }}
                        >
                          <Stack spacing={2} sx={{ flex: 1 }}>
                            <StyleImageTile
                              candidates={getStyleImageCandidates(
                                result.imageStyles[0],
                                secondaryIndex
                              )}
                              alt={`${result.displayName} style detail 1`}
                              height={{ xs: 180, md: 200 }}
                            />
                            <StyleImageTile
                              candidates={getStyleImageCandidates(
                                result.imageStyles[0],
                                tertiaryIndex
                              )}
                              alt={`${result.displayName} style detail 2`}
                              height={{ xs: 180, md: 200 }}
                            />
                          </Stack>
                          <Box sx={{ flex: 2 }}>
                            <StyleImageTile
                              candidates={getStyleImageCandidates(
                                result.imageStyles[0],
                                primaryIndex
                              )}
                              alt={`${result.displayName} style hero`}
                              height={{ xs: 260, md: 420 }}
                            />
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  );
                })
              ) : (
                <Typography sx={{ color: "#9CA3AF" }}>
                  No design style results yet.
                </Typography>
              )}
            </Stack>

            <Box
              sx={{
                border: "1px solid #E6E8EE",
                borderRadius: 2,
                p: { xs: 2, md: 3 },
                backgroundColor: "#fff",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  sx={{ fontWeight: 700, color: "#111827", fontSize: "1.1rem" }}
                >
                  My Idea Books
                </Typography>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    size="small"
                    sx={{ color: ACCENT_COLOR }}
                    disabled={!canGoPrev}
                    onClick={() =>
                      setIdeaBooksPage((prev) => Math.max(0, prev - 1))
                    }
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: ACCENT_COLOR }}
                    disabled={!canGoNext}
                    onClick={() =>
                      setIdeaBooksPage((prev) =>
                        Math.min(maxIdeaBooksPage, prev + 1)
                      )
                    }
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Stack>
              </Stack>
              {isLoadingIdeaBooks ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} sx={{ color: ACCENT_COLOR }} />
                </Box>
              ) : ideaBooks.length === 0 ? (
                <Typography sx={{ mt: 2, color: "#9CA3AF" }}>
                  No idea books yet.
                </Typography>
              ) : (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {visibleIdeaBooks.map((book) => {
                    const coverUrl =
                      book.coverImage?.url ?? book.coverImage?.path ?? "";
                    const itemCountLabel = `${book.itemCount} idea${
                      book.itemCount === 1 ? "" : "s"
                    }`;

                    return (
                      <Grid item xs={12} sm={6} md={3} key={book._id}>
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            isDesignerViewer
                              ? handleOpenIdeaBook(book)
                              : navigate(
                                  Pages.MY_IDEA_BOOK_DETAILS.replace(
                                    ":ideaBookId",
                                    book._id
                                  )
                                )
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              if (isDesignerViewer) {
                                handleOpenIdeaBook(book);
                              } else {
                                navigate(
                                  Pages.MY_IDEA_BOOK_DETAILS.replace(
                                    ":ideaBookId",
                                    book._id
                                  )
                                );
                              }
                            }
                          }}
                          sx={{
                            border: "1px solid #E5E7EB",
                            borderRadius: 2,
                            overflow: "hidden",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                            outline: "none",
                            "&:focus-visible": {
                              boxShadow:
                                "0 0 0 3px rgba(194, 24, 91, 0.2), 0 2px 8px rgba(0, 0, 0, 0.08)",
                            },
                          }}
                        >
                          <Box sx={{ position: "relative" }}>
                            {coverUrl ? (
                              <Box
                                component="img"
                                src={coverUrl}
                                alt={book.name}
                                loading="lazy"
                                sx={{
                                  width: "100%",
                                  height: 140,
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 140,
                                  backgroundColor: "#F3F4F6",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <ImageOutlinedIcon sx={{ color: "#9CA3AF" }} />
                              </Box>
                            )}
                            <Box
                              sx={{
                                position: "absolute",
                                left: 10,
                                bottom: 10,
                                px: 1,
                                py: 0.3,
                                borderRadius: "999px",
                                backgroundColor: "rgba(17, 24, 39, 0.85)",
                                color: "#fff",
                                fontSize: 11,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <ImageOutlinedIcon sx={{ fontSize: 13 }} />
                              {book.itemCount}
                            </Box>
                          </Box>
                          <Box sx={{ p: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: "#111827",
                                fontSize: "0.9rem",
                              }}
                            >
                              {book.name}
                            </Typography>
                            <Typography
                              sx={{ color: "#6B7280", fontSize: "0.75rem" }}
                            >
                              {itemCountLabel}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </Stack>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ pt: { xs: 0, md: 6 } }}>
            <Typography sx={{ fontWeight: 700, color: "#111827" }}>
              Design Concepts:
            </Typography>
            {designConceptLabels.length ? (
              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                {designConceptLabels.map((label) => (
                  <Typography
                    key={label}
                    sx={{ color: "#4F46E5", fontWeight: 600 }}
                  >
                    {label}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ mt: 1.5, color: "#9CA3AF" }}>
                Not provided
              </Typography>
            )}
            {showPreferredRendering ? (
              <Chip
                label="Preferred 3D Renderings"
                size="small"
                icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  mt: 2,
                  backgroundColor: "#FDF2F8",
                  color: ACCENT_COLOR,
                  borderColor: ACCENT_COLOR,
                  borderWidth: 1,
                  borderStyle: "solid",
                  fontWeight: 600,
                }}
              />
            ) : null}
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={ideaBookDialogOpen}
        onClose={handleCloseIdeaBook}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {activeIdeaBook?.name ?? "Idea Book"}
          <IconButton
            onClick={handleCloseIdeaBook}
            sx={{ position: "absolute", right: 12, top: 12 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingIdeaBookDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} sx={{ color: ACCENT_COLOR }} />
            </Box>
          ) : activeIdeaBookItems.length === 0 ? (
            <Typography sx={{ color: "#9CA3AF" }}>
              No items in this idea book yet.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {activeIdeaBookItems.map((item) => {
                const imageUrl = item.image?.url ?? item.image?.path ?? "";
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                    <Box
                      role={imageUrl ? "button" : undefined}
                      tabIndex={imageUrl ? 0 : -1}
                      onClick={() => {
                        if (!imageUrl) return;
                        setPreviewImageUrl(imageUrl);
                      }}
                      onKeyDown={(event) => {
                        if (!imageUrl) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setPreviewImageUrl(imageUrl);
                        }
                      }}
                      sx={{
                        border: "1px solid #E5E7EB",
                        borderRadius: 2,
                        overflow: "hidden",
                        backgroundColor: "#fff",
                        cursor: imageUrl ? "pointer" : "default",
                        outline: "none",
                        "&:focus-visible": {
                          boxShadow:
                            "0 0 0 3px rgba(194, 24, 91, 0.2), 0 2px 8px rgba(0, 0, 0, 0.08)",
                        },
                      }}
                    >
                      {imageUrl ? (
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={item.image?.title ?? "Idea book item"}
                          loading="lazy"
                          sx={{
                            width: "100%",
                            height: 160,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: 160,
                            backgroundColor: "#F3F4F6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ImageOutlinedIcon sx={{ color: "#9CA3AF" }} />
                        </Box>
                      )}
                      {item.note ? (
                        <Box sx={{ p: 1.5, borderTop: "1px solid #E5E7EB" }}>
                          <Typography sx={{ fontSize: "0.8rem", color: "#6B7280" }}>
                            {item.note}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
          <Button onClick={handleCloseIdeaBook}>Close</Button>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(previewImageUrl)}
        onClose={() => setPreviewImageUrl(null)}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: "#F5F5F5",
          },
        }}
      >
        <DialogContent
          sx={{
            p: { xs: 2, md: 4 },
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              onClick={() => setPreviewImageUrl(null)}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color: "#111827",
                backgroundColor: "rgba(255, 255, 255, 0.92)",
                border: "1px solid #E5E7EB",
                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.16)",
                "&:hover": {
                  backgroundColor: "#fff",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            {previewImageUrl && (
              <Box
                component="img"
                src={previewImageUrl}
                alt="Idea book preview"
                sx={{
                  maxWidth: "96vw",
                  maxHeight: "90vh",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                  borderRadius: "16px",
                  backgroundColor: "#fff",
                  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
                }}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

export default DesignStyleTab;
